import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { UpsertGoalModal } from '../../components/UpsertGoalModal/UpsertGoalModal';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { FormValues, HealthCareData, UpsertDTO } from './HealthCare.meta';
import { useHealthCare } from './useHealthCare';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { Radio } from '../../../../CommonComponents/OPL/Radio/Radio';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { EstimateExpensesTooltip } from './components/EstimateExpensesTooltip/EstimateExpensesTooltip';
import { useUpsertGoalMutation } from '../../api/useUpsertGoalMutation';
import { AT_RETIREMENT } from '../../Goals.meta';
import { GoalComponentProps } from '../../components/Goal/Goal.meta';
import { AssetBucketingSelect } from '../../components/AssetBucketingSelect/AssetBucketingSelect';

const FORM_ID = 'ProfileGoals--HealthCare';

interface Props extends GoalComponentProps {
  goalData: HealthCareData;
}

export const HealthCare = ({
  clientId,
  goalData,
  yearsOptions,
  assetBucketingIndices,
  ...rest
}: Props) => {
  const {
    startYearOptions,
    startDate,
    startAtRetirement,
    estimateExpenses,
    isCanadian,
    annualExpense,
    inflation,
  } = useHealthCare({
    goalData,
    yearsOptions,
  });

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: {
      startDate: startAtRetirement ? AT_RETIREMENT : startDate,
      startAtRetirement,
      estimateExpenses: isCanadian ? false : !!estimateExpenses,
      annualExpense,
      inflation,
      assetBucketingIndices,
    },
  });

  const watchedEstimateExpenses = watch('estimateExpenses');

  const {
    mutateAsync: upsertGoal,
    isLoading: isUpsertingGoal,
    error: upsertGoalError,
  } = useUpsertGoalMutation({ clientId, url: '/Consumer/GoalUpdate/SaveHealthCareGoal' });

  const onSubmit = async ({ startDate: _startDate, ...values }: FormValues) => {
    try {
      const upsertDTO: UpsertDTO = {
        ...values,
        startYear: values.startAtRetirement ? null : _startDate,
      };

      await upsertGoal(upsertDTO);

      rest.onClose();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <UpsertGoalModal clientId={clientId} formId={FORM_ID} isLoading={isUpsertingGoal} {...rest}>
      {upsertGoalError && <Alert message={upsertGoalError.message} variant="error" mb="16" />}

      <Box
        id={FORM_ID}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        display="grid"
        css={`
          grid-template-columns: 1fr;
          gap: 24px;
        `}
        aria-label="Healthcare goal form"
      >
        <Controller
          control={control}
          name="startDate"
          render={({ field: { value, onChange } }) => (
            <Select
              label="Start Year"
              value={value ? ({ value, label: value } as any) : null}
              options={startYearOptions}
              onChange={opt => {
                onChange(opt?.value);
                setValue('startAtRetirement', opt?.value === AT_RETIREMENT);
              }}
              isPortal
            />
          )}
        />

        {!isCanadian && (
          <Box
            as="fieldset"
            aria-labelledby="health-care-goal-form--cost-estimate-label"
            css="border: 0; padding: 0; margin: 0;"
          >
            <Text
              as="legend"
              variant="formLabel"
              color="neutral.600"
              id="health-care-goal-form--cost-estimate-label"
              css="display: block"
              mb="4"
              p="0"
            >
              Cost Estimate
            </Text>
            <Controller
              name="estimateExpenses"
              control={control}
              render={({ field: { value, name: _name, onChange } }) => (
                <Box display="flex" flexDirection="column" css="gap: 8px">
                  <Radio name={_name} checked={value} onChange={() => onChange(true)}>
                    Automatically estimate healthcare costs <EstimateExpensesTooltip />
                  </Radio>

                  <Radio name={_name} checked={!value} onChange={() => onChange(false)}>
                    Use my own estimate
                  </Radio>
                </Box>
              )}
            />
          </Box>
        )}

        {!watchedEstimateExpenses && (
          <>
            <Controller
              control={control}
              name="annualExpense"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label={
                    <>
                      Annual Expense{' '}
                      <Text as="span" color="neutral.500" fontSize="small">
                        (Today&apos;s Dollars)
                      </Text>
                    </>
                  }
                  leftIcon="far fa-dollar-sign"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="inflation"
              render={({ field: { onChange, ...field } }) => (
                <InputNumberMask
                  label="Inflation"
                  rightIcon="far fa-percent"
                  decimalScale={2}
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  {...field}
                />
              )}
            />
          </>
        )}

        <AssetBucketingSelect clientId={clientId} control={control} />
      </Box>
    </UpsertGoalModal>
  );
};
